import { Injectable } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { FiCommentSettingType, ObjectTypes } from '@fi-shared/types';
import { ICommentModel, ICommentsContainer, ICommentsCountItem } from '@fi-shared/fi-comments/fi-comments';
import { Observable, Subject } from 'rxjs';
import { FiCommentAddScreenComponent } from '@fi-shared/fi-comments/fi-comments-list/fi-comment-add/fi-comment-add-screen.component';
import { FicScreenService } from '@fi-shared/fic-screen/fic-screen.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { takeUntil } from 'rxjs/operators';

@Injectable()
export class FiCommentsService {
    constructor(
        private fiBackend: FiBackendService,
        private ficScreenService: FicScreenService,
        private userDataService: FiUserDataService,
    ) {}

    getComments(objectId: number, objectType: ObjectTypes) {
        return this.fiBackend.fetchData<ICommentModel[]>(
            'Core/Common/GetCommentsByContentLanguageId/' +
                objectId +
                '/' +
                objectType +
                '/' +
                0 +
                '/' +
                -1 +
                '/' +
                0 +
                '/' +
                FiCommentSettingType.AllowAllComments,
        );
    }

    // TODO: remove this method
    getNewCommentInstance(objectId: number, objectType: ObjectTypes, parentCommentId: number) {
        const comment: ICommentModel = {
            ObjectId: objectId,
            ObjectType: objectType,
            Content: undefined,
            ChildItems: [],
            ParentId: parentCommentId,
            UserProfile: this.userDataService.getUserProfile(),
            MediaBankFileReferences: [],
            Links: [],
            Reactions: [],
            TaggedUsers: [],
        };
        return comment;
    }

    // TODO: remove this method
    getFlattenComments(comments: ICommentModel[]) {
        let allComments: ICommentModel[] = [];
        const getCommentsRecursively = (comments: ICommentModel[]) => {
            comments.forEach((comment) => {
                allComments.push(comment);
                if (comment.ChildItems) getCommentsRecursively(comment.ChildItems);
            });
        };
        getCommentsRecursively(comments);
        return allComments;
    }

    saveComment(comment: ICommentModel) {
        return this.fiBackend.fetchDataPost<number>('Core/Common/SaveComment', comment);
    }

    deleteComments(commentIds: number[]) {
        return this.fiBackend.fetchDataPost<void>('Core/Common/DeleteComments', commentIds);
    }

    createCommentsContainer(
        comments: ICommentModel[],
        objectId: number,
        objectType: ObjectTypes,
        ngOnDestroy$: Observable<void>,
        hasDeleteAccessOfOwnerItem: boolean,
        commentsToShowLimit?: number,
    ): ICommentsContainer {
        const commentsUpdated$ = new Subject<void>();
        ngOnDestroy$.subscribe(() => commentsUpdated$.complete());
        let viewAllCommentsVisible = false;
        let commentIdsToShow: number[] = undefined;
        if (commentsToShowLimit) {
            const allComments = this.getFlattenComments(comments);
            viewAllCommentsVisible = allComments.length > commentsToShowLimit;
            commentIdsToShow = allComments.slice(0, commentsToShowLimit).map((comment) => comment.Id);

            commentsUpdated$.pipe(takeUntil(ngOnDestroy$)).subscribe(() => {
                viewAllCommentsVisible = false;
                commentIdsToShow = undefined;
            });
        }

        return {
            comments: comments,
            objectId: objectId,
            objectType: objectType,
            hasDeleteAccessOfOwnerItem: hasDeleteAccessOfOwnerItem,
            commentsUpdated$: commentsUpdated$.asObservable(),
            raiseCommentsUpdated: () => commentsUpdated$.next(),
            isCommentVisible: (comment: ICommentModel) =>
                !commentIdsToShow || commentIdsToShow.indexOf(comment.Id) !== -1,
            isViewAllCommentsVisible: () => viewAllCommentsVisible,
            viewAllComments: () => {
                viewAllCommentsVisible = false;
                commentsUpdated$.next();
            },
        };
    }

    // todo: remove this method
    openAddCommentScreen(objectId: number, objectType: ObjectTypes, instanceId: string, parentCommentId?: number) {
        return this.ficScreenService.openScreen({
            componentType: FiCommentAddScreenComponent,
            data: {
                objectId: objectId,
                objectType: objectType,
                parentCommentId: parentCommentId,
                instanceId: instanceId,
            },
        });
    }

    getCommentsCount(args: { objectTypes?: ObjectTypes[]; objectIds?: number[]; forumIds?: number[] }) {
        return this.fiBackend.fetchDataPost<ICommentsCountItem[]>('Core/Common/GetCommentsCount', {
            ObjectIds: args.objectIds,
            ObjectTypes: args.objectTypes,
            ForumIds: args.forumIds,
        });
    }
}
